import React, { useState, useEffect, useCallback } from 'react';
import './EmployeeDirectory.css';
import AddEmployeeModal from './AddEmployeeModal';
import EditEmployeeModal from './EditEmployeeModal';
import logo from '../assets/Zenio+Logo+header.png';
import { api, useLocalStorageFallback } from '../services/api';

function EmployeeDirectory({ user, onBack }) {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const useFallback = useLocalStorageFallback();

  const loadEmployees = useCallback(async () => {
    setIsLoading(true);
    setError('');

    if (useFallback) {
      // Use localStorage
      const savedEmployees = localStorage.getItem('zenio_employees');
      if (savedEmployees) {
        setEmployees(JSON.parse(savedEmployees));
      } else {
        setEmployees([]);
      }
      setIsLoading(false);
    } else {
      // Use API
      try {
        const result = await api.getEmployees();
        if (result.success && result.employees) {
          setEmployees(result.employees);
        } else {
          setEmployees([]);
        }
      } catch (error) {
        console.error('Failed to load employees:', error);
        setError('Failed to load employees');
        setEmployees([]);
      } finally {
        setIsLoading(false);
      }
    }
  }, [useFallback]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleAddEmployee = async (newEmployee) => {
    if (useFallback) {
      const employeeWithId = {
        ...newEmployee,
        employeeId: Date.now().toString(),
        createdAt: new Date().toISOString()
      };
      const updatedEmployees = [...employees, employeeWithId];
      setEmployees(updatedEmployees);
      localStorage.setItem('zenio_employees', JSON.stringify(updatedEmployees));
      setShowAddModal(false);
    } else {
      try {
        const result = await api.createEmployee(newEmployee);
        if (result.success) {
          await loadEmployees();
          setShowAddModal(false);
        } else {
          setError('Failed to add employee');
        }
      } catch (error) {
        console.error('Failed to add employee:', error);
        setError('Failed to add employee');
      }
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedEmployee) => {
    if (useFallback) {
      const updatedEmployees = employees.map(emp =>
        emp.employeeId === updatedEmployee.employeeId ? updatedEmployee : emp
      );
      setEmployees(updatedEmployees);
      localStorage.setItem('zenio_employees', JSON.stringify(updatedEmployees));
      setShowEditModal(false);
      setEditingEmployee(null);
    } else {
      try {
        const result = await api.updateEmployee(updatedEmployee.employeeId, updatedEmployee);
        if (result.success) {
          await loadEmployees();
          setShowEditModal(false);
          setEditingEmployee(null);
        } else {
          setError('Failed to update employee');
        }
      } catch (error) {
        console.error('Failed to update employee:', error);
        setError('Failed to update employee');
      }
    }
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this employee?')) {
      return;
    }

    if (useFallback) {
      const updatedEmployees = employees.filter(emp => emp.employeeId !== employeeId);
      setEmployees(updatedEmployees);
      localStorage.setItem('zenio_employees', JSON.stringify(updatedEmployees));
    } else {
      try {
        const result = await api.deleteEmployee(employeeId);
        if (result.success) {
          await loadEmployees();
        } else {
          setError('Failed to delete employee');
        }
      } catch (error) {
        console.error('Failed to delete employee:', error);
        setError('Failed to delete employee');
      }
    }
  };

  // Get unique departments for filter
  const departments = ['all', ...new Set(employees.map(emp => emp.department).filter(Boolean))];

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || emp.department === filterDepartment;
    return matchesSearch && matchesDepartment;
  });

  return (
    <div className="employee-directory">
      <div className="directory-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <div className="header-logo-section">
          <div className="header-logo-backdrop">
            <img src={logo} alt="ZenIO Logo" className="header-logo-img" />
          </div>
          <h1>Employee Directory (FOR DEMO USE ONLY)</h1>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="directory-controls">
        <input
          type="text"
          className="search-input"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isLoading}
        />
        
        <select
          className="department-filter"
          value={filterDepartment}
          onChange={(e) => setFilterDepartment(e.target.value)}
          disabled={isLoading}
        >
          {departments.map(dept => (
            <option key={dept} value={dept}>
              {dept === 'all' ? 'All Departments' : dept}
            </option>
          ))}
        </select>

        {user.isAdmin && (
          <button
            className="add-employee-button"
            onClick={() => setShowAddModal(true)}
            disabled={isLoading}
          >
            + Add Employee
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading-message">Loading employees...</div>
      ) : (
        <>
          {filteredEmployees.length === 0 ? (
            <div className="no-employees">
              <p>No employees found</p>
              {user.isAdmin && (
                <button
                  className="add-employee-button"
                  onClick={() => setShowAddModal(true)}
                >
                  Add First Employee
                </button>
              )}
            </div>
          ) : (
            <div className="employees-grid">
              {filteredEmployees.map(employee => (
                <div key={employee.employeeId} className="employee-card">
                  {user.isAdmin && (
                    <div className="card-actions">
                      <button
                        className="edit-button"
                        onClick={() => handleEditEmployee(employee)}
                        title="Edit employee"
                      >
                        ✎
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteEmployee(employee.employeeId)}
                        title="Remove employee"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  
                  <div className="employee-photo">
                    {employee.photo ? (
                      <img src={employee.photo} alt={employee.name} />
                    ) : (
                      <div className="placeholder-photo">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>

                  <div className="employee-info">
                    <h3 className="employee-name">{employee.name}</h3>
                    {employee.title && (
                      <p className="employee-title">{employee.title}</p>
                    )}
                    {employee.department && (
                      <p className="employee-department">{employee.department}</p>
                    )}
                  </div>

                  <div className="employee-contact">
                    {employee.email && (
                      <a href={`mailto:${employee.email}`} className="contact-link">
                        📧 {employee.email}
                      </a>
                    )}
                    {employee.phone && (
                      <a href={`tel:${employee.phone}`} className="contact-link">
                        📞 {employee.phone}
                      </a>
                    )}
                    {employee.extension && (
                      <p className="employee-extension">Ext: {employee.extension}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showAddModal && (
        <AddEmployeeModal
          onAdd={handleAddEmployee}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showEditModal && editingEmployee && (
        <EditEmployeeModal
          employee={editingEmployee}
          onSave={handleSaveEdit}
          onClose={() => {
            setShowEditModal(false);
            setEditingEmployee(null);
          }}
        />
      )}
    </div>
  );
}

export default EmployeeDirectory;
